import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Header,
    SpaceBetween,
    Box,
    Button,
    TableProps,
    FormField,
    Modal,
    Input,
    ButtonDropdown
} from '@amzn/awsui-components-react';
import { useSelector } from 'react-redux';
import {
    exportDataToCSV,
    exportDataToJson,
    exportDataToXLSX,
    getErrorMessage,
    validatePlmId
} from '../../utils/commons';
import {
    operationConfigs,
    OperationType,
    DynamicPartInput,
    subscribePart,
    subscribePartsList,
    unsubscribePart,
    unsubscribePartsList,
    updatePart,
    updatePartList,
    updateAllParts
} from './partSubscriptionUtils';
import { RetrievedPartData } from '../../open-api/generated-src/api';
import { AddFlashMessageType } from '../hooks/useFlashMessage';
import { RootState } from '../../state/store';

// Table header content, shows how many parts are selected and contains the action stripe
interface PartsTableHeaderProps {
    parts: RetrievedPartData[];
    isPartsLoading: boolean;
    filteringText: string;
    selectedParts: RetrievedPartData[];
    counter: string;
    columnDefinitions: TableProps.ColumnDefinition<RetrievedPartData>[];
    addFlashMessage: AddFlashMessageType;
    retrievePartsCallback: () => Promise<void>;
    searchPartsCallback: (filteringText: string) => Promise<void>;
}

const DISABLED_REASON_NO_SELECTION = 'No part selected';
const DISABLED_REASON_NO_VALID_SELECTION =
    'No valid parts selected. Selected parts are missing manufacturer or manufacturer part number.';

export default function PartsTableHeader({
    parts,
    isPartsLoading,
    filteringText,
    selectedParts,
    counter,
    columnDefinitions,
    addFlashMessage,
    retrievePartsCallback,
    searchPartsCallback
}: PartsTableHeaderProps) {
    const plmId = useSelector((root: RootState) => root.plm.plmId);
    const [modalVisible, setModalVisible] = useState<boolean>(false);
    const [isSubscribeButtonEnabled, setIsSubscribeButtonEnabled] = useState<boolean>(false);
    const [isUnsubscribeButtonEnabled, setIsUnsubscribeButtonEnabled] = useState<boolean>(false);
    const [isUpdateButtonEnabled, setIsUpdateButtonEnabled] = useState<boolean>(false);
    const [isUpdateAllButtonEnabled, setIsUpdateAllButtonEnabled] = useState<boolean>(true);
    const [isActionsLoading, setIsActionsLoading] = useState<boolean>(false);
    const location = useLocation();
    const navigate = useNavigate();

    const validSelectedParts = useMemo(() => {
        return selectedParts
            .filter(
                (
                    part
                ): part is Required<
                    Pick<RetrievedPartData, 'manufacturer' | 'manufacturerPartNumber'>
                > &
                    RetrievedPartData => !!part.manufacturer && !!part.manufacturerPartNumber
            )
            .map((part) => ({
                manufacturer: part.manufacturer,
                manufacturerPartNumber: part.manufacturerPartNumber,
                internalPartNumber: part.internalPartNumber
            }));
    }, [selectedParts]);

    const handleZ2DataOperation = async <T extends OperationType>(
        operation: T,
        setButtonEnabled: (enabled: boolean) => void,
        operationSingePartFunction: (part: DynamicPartInput<T>) => Promise<void>,
        operationListPartsFunction: (parts: DynamicPartInput<T>[]) => Promise<void>
    ) => {
        setButtonEnabled(false);
        setIsActionsLoading(true);
        const isSinglePart = validSelectedParts.length === 1;

        try {
            if (validSelectedParts.length === 0) {
                addFlashMessage({
                    type: 'error',
                    dismissible: true,
                    content: DISABLED_REASON_NO_VALID_SELECTION
                });
                return;
            }

            const func = isSinglePart
                ? () => operationSingePartFunction(validSelectedParts[0] as DynamicPartInput<T>)
                : () => operationListPartsFunction(validSelectedParts as DynamicPartInput<T>[]);

            await func();
            addFlashMessage({
                type: 'success',
                dismissible: true,
                content: isSinglePart
                    ? operationConfigs[operation].successMessageSingle
                    : operationConfigs[operation].successMessageMultiple,
                action: <Button onClick={retrievePartsCallback}>Refresh</Button>
            });
        } catch (error) {
            addFlashMessage({
                type: 'error',
                dismissible: true,
                content: isSinglePart
                    ? operationConfigs[operation].errorMessageSingle(error)
                    : operationConfigs[operation].errorMessageMultiple(error)
            });
        } finally {
            setButtonEnabled(true);
            setIsActionsLoading(false);
        }
    };

    const subscribeToZ2DataHandler = () =>
        handleZ2DataOperation<OperationType.Subscribe>(
            OperationType.Subscribe,
            setIsSubscribeButtonEnabled,
            subscribePart,
            subscribePartsList
        );

    const unsubscribeToZ2DataHandler = () =>
        handleZ2DataOperation<OperationType.Unsubscribe>(
            OperationType.Unsubscribe,
            setIsUnsubscribeButtonEnabled,
            unsubscribePart,
            unsubscribePartsList
        );

    const handleUpdateSelectedParts = async () => {
        if (!validatePlmId(plmId, addFlashMessage)) return;
        setIsActionsLoading(true);
        setIsUpdateButtonEnabled(false);
        const updateInputs = validSelectedParts.map((item) => ({
            manufacturer: item.manufacturer,
            manufacturerPartNumber: item.manufacturerPartNumber
        }));
        const isSinglePart = updateInputs.length === 1;
        try {
            if (updateInputs.length === 0) {
                addFlashMessage({
                    type: 'error',
                    dismissible: true,
                    content: DISABLED_REASON_NO_VALID_SELECTION
                });
                return;
            }
            const response = isSinglePart
                ? await updatePart({
                      ...updateInputs[0],
                      plmId
                  })
                : await updatePartList({ parts: updateInputs, plmId });

            addFlashMessage({
                type: 'success',
                dismissible: true,
                content: response.statusMessage
            });
        } catch (error) {
            addFlashMessage({
                type: 'error',
                dismissible: true,
                content: `Failed to update ${updateInputs.length} ${isSinglePart ? 'part' : 'parts'} in PLM. ${getErrorMessage(error)}`
            });
        } finally {
            setIsUpdateButtonEnabled(true);
            setIsActionsLoading(false);
        }
    };

    const handleUpdateAllParts = async () => {
        if (!validatePlmId(plmId, addFlashMessage)) return;
        setIsUpdateAllButtonEnabled(false);
        try {
            const response = await updateAllParts({ plmId });
            addFlashMessage({
                type: 'success',
                dismissible: true,
                content: response.statusMessage
            });
        } catch (error) {
            addFlashMessage({
                type: 'error',
                dismissible: true,
                content: `Failed to start PLM update job. ${getErrorMessage(error)}`
            });
        } finally {
            setIsUpdateAllButtonEnabled(true);
        }
    };

    const handleSubscribeOrUpdate = (id: string) => {
        switch (id) {
            case 'subscribeToZ2Data':
                subscribeToZ2DataHandler();
                break;
            case 'unsubscribeFromZ2Data':
                unsubscribeToZ2DataHandler();
                break;
            case 'updateToPLM':
                handleUpdateSelectedParts();
                break;
            case 'updatetoAllPLM':
                handleUpdateAllParts();
                break;
            default:
                break;
        }
    };

    const handleExport = (id: string) => {
        const dataToExport = id.startsWith('all') ? parts : selectedParts;
        const fileName = id.startsWith('all') ? 'all_parts' : 'selected_parts';
        switch (id) {
            case 'all_excel':
            case 'selected_excel':
                exportDataToXLSX(dataToExport, `${fileName}.xlsx`, columnDefinitions);
                break;
            case 'all_csv':
            case 'selected_csv':
                exportDataToCSV('text/csv', dataToExport, `${fileName}.csv`, columnDefinitions);
                break;
            case 'all_json':
            case 'selected_json':
                exportDataToJson(
                    'application/json',
                    dataToExport,
                    `${fileName}.json`,
                    columnDefinitions
                );
                break;
        }
    };

    useEffect(() => {
        const hasSelectedParts = selectedParts.length > 0;
        setIsSubscribeButtonEnabled(hasSelectedParts);
        setIsUnsubscribeButtonEnabled(hasSelectedParts);
        setIsUpdateButtonEnabled(hasSelectedParts);
    }, [selectedParts.length]);

    return (
        <Header
            variant='h1'
            counter={counter}
            actions={
                <SpaceBetween direction='horizontal' size='s'>
                    <Button
                        onClick={() => {
                            if (filteringText !== '') {
                                searchPartsCallback(filteringText);
                            } else {
                                retrievePartsCallback();
                            }
                        }}
                        ariaLabel='Refresh'
                        iconName='refresh'
                        loading={isPartsLoading}
                        loadingText={'Refreshing Parts'}
                    />
                    {/* TODO: uncomment after implement the part detail page
                    <Button disabled={!isOnlyOneSelected} onClick={onClickHandler}>
                        View details
                    </Button> */}
                    <ButtonDropdown
                        onItemClick={(e) => handleSubscribeOrUpdate(e.detail.id)}
                        loading={isActionsLoading}
                        items={[
                            {
                                text: 'Subscribe to Z2Data',
                                id: 'subscribeToZ2Data',
                                disabled: !isSubscribeButtonEnabled,
                                disabledReason: DISABLED_REASON_NO_SELECTION
                            },
                            {
                                text: 'Unsubscribe from Z2Data',
                                id: 'unsubscribeFromZ2Data',
                                disabled: !isUnsubscribeButtonEnabled,
                                disabledReason: DISABLED_REASON_NO_SELECTION
                            },
                            {
                                text: 'Update to PLM',
                                id: 'updateToPLM',
                                disabled: !isUpdateButtonEnabled,
                                disabledReason: DISABLED_REASON_NO_SELECTION
                            },
                            {
                                text: 'Update all to PLM',
                                id: 'updatetoAllPLM',
                                disabled: !isUpdateAllButtonEnabled
                            }
                        ]}
                    >
                        Actions
                    </ButtonDropdown>
                    <ButtonDropdown
                        onItemClick={(e) => handleExport(e.detail.id)}
                        items={[
                            {
                                text: 'Download All Parts(CSV)',
                                id: 'all_csv'
                            },
                            {
                                text: 'Download All Parts(EXCEL)',
                                id: 'all_excel'
                            },
                            {
                                text: 'Download All Parts(JSON)',
                                id: 'all_json'
                            },
                            {
                                text: 'Download Selected Parts(CSV)',
                                id: 'selected_csv',
                                disabled: selectedParts.length === 0,
                                disabledReason: DISABLED_REASON_NO_SELECTION
                            },
                            {
                                text: 'Download Selected Parts(EXCEL)',
                                id: 'selected_excel',
                                disabled: selectedParts.length === 0,
                                disabledReason: DISABLED_REASON_NO_SELECTION
                            },
                            {
                                text: 'Download Selected Parts(JSON)',
                                id: 'selected_json',
                                disabled: selectedParts.length === 0,
                                disabledReason: DISABLED_REASON_NO_SELECTION
                            }
                        ]}
                    >
                        Export Parts
                    </ButtonDropdown>
                    <ButtonDropdown
                        onItemClick={(e) => {
                            if (e.detail.id === 'add-a-part') {
                                navigate(`${location.pathname}/add-a-part`);
                            } else {
                                navigate(`${location.pathname}/import-multiple-parts-from-CSV`);
                            }
                        }}
                        items={[
                            {
                                text: 'Add a Part',
                                id: 'add-a-part'
                            },
                            {
                                text: 'Import Multiple Parts from CSV',
                                id: 'import-multiple-parts-from-CSV'
                            }
                        ]}
                        variant='primary'
                    >
                        Add Part(s)
                    </ButtonDropdown>
                    <Modal
                        onDismiss={() => setModalVisible(false)}
                        visible={modalVisible}
                        footer={
                            <Box float='right'>
                                <SpaceBetween direction='horizontal' size='xs'>
                                    <Button variant='link' onClick={() => setModalVisible(false)}>
                                        Cancel
                                    </Button>
                                    <Button
                                        variant='primary'
                                        onClick={() => setModalVisible(false)}
                                    >
                                        Ok
                                    </Button>
                                </SpaceBetween>
                            </Box>
                        }
                        header='Add Compliance Retriever'
                    >
                        <SpaceBetween direction='vertical' size='l'>
                            <FormField label='Part Id'>
                                <Input value='' onChange={() => {}} />
                            </FormField>
                            <FormField label='Retrieval Source'>
                                <Input value='' onChange={() => {}} />
                            </FormField>
                        </SpaceBetween>
                    </Modal>
                </SpaceBetween>
            }
        >
            Parts
        </Header>
    );
}
